import React, { useState, useRef, useEffect, useCallback } from 'react'
import classnames from 'classnames'
import styles from './styles.less'
import { T } from '../i18n'
import { getFlagStyle } from './languageConfig'

const LanguageSelector = ({
  id,
  className,
  options,
  selectedLocale,
  pendingLocale,
  onSelect
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const containerRef = useRef(null)
  const hoverCloseTimeoutRef = useRef(null)
  const hasMultipleOptions = options.length > 1

  const clearHoverTimeout = useCallback(() => {
    if (!hoverCloseTimeoutRef.current) return
    clearTimeout(hoverCloseTimeoutRef.current)
    hoverCloseTimeoutRef.current = null
  }, [])

  const closeMenu = useCallback(() => {
    clearHoverTimeout()
    setIsOpen(false)
  }, [clearHoverTimeout])

  const handleMouseEnter = useCallback(() => {
    clearHoverTimeout()
    if (!hasMultipleOptions) return
    setIsOpen(true)
  }, [hasMultipleOptions, clearHoverTimeout])

  const handleMouseLeave = useCallback((event) => {
    if (containerRef.current?.contains(event.relatedTarget)) return
    clearHoverTimeout()
    if (!hasMultipleOptions) return
    hoverCloseTimeoutRef.current = setTimeout(() => {
      hoverCloseTimeoutRef.current = null
      closeMenu()
    }, 1000)
  }, [hasMultipleOptions, closeMenu, clearHoverTimeout])

  useEffect(() => () => clearHoverTimeout(), [clearHoverTimeout])

  useEffect(() => {
    if (!isOpen) return
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target))
        closeMenu()
    }
    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('touchstart', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('touchstart', handleClickOutside)
    }
  }, [isOpen, closeMenu])

  useEffect(() => {
    if (!isOpen) return
    const handleKeyDown = (event) => {
      if (event.key === 'Escape')
        closeMenu()
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [isOpen, closeMenu])

  const handleNativeChange = (event) => {
    onSelect(event)
    closeMenu()
  }

  const handleOptionSelect = (value) => {
    if (!value || value === selectedLocale || pendingLocale === value) {
      closeMenu()
      return
    }
    onSelect({ target: { value } })
    closeMenu()
  }

  const listId = `${id}-list`
  const tooltipId = `${id}-tooltip`

  return (
    <div
      className={classnames(className, styles.languageSelectorRoot)}
      ref={containerRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <label className="sr-only" htmlFor={id}>Language</label>
      <select
        id={id}
        className={styles.languageSelect}
        value={selectedLocale}
        onChange={handleNativeChange}
      >
        {options.map(option => (
          <option value={option.value} key={option.value}>{option.label}</option>
        ))}
      </select>
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-controls={listId}
        aria-describedby={tooltipId}
        className={classnames(styles.languageTrigger, {
          [styles.languageTriggerActive]: isOpen
        })}
        onClick={() => setIsOpen(prev => !prev)}
      >
        <span
          className={styles.languageFlag}
          style={getFlagStyle(selectedLocale)}
          aria-hidden="true"
        />
        <span className="sr-only">Toggle language menu</span>
      </button>
      <span id={tooltipId} className={styles.languageTooltip}>
        <T
          id="header.language.tooltip"
          defaultMessage="Only translate the text, currency and VAT follow the shop's country."
        />
      </span>
      {isOpen && (
        <ul className={styles.languageList} id={listId} role="listbox">
          {options.map(option => (
            <li key={option.value} className={styles.languageListItem}>
              <button
                type="button"
                role="option"
                aria-selected={selectedLocale === option.value}
                className={classnames(styles.languageOption, {
                  [styles.languageOptionActive]: selectedLocale === option.value
                })}
                onClick={() => handleOptionSelect(option.value)}
                disabled={pendingLocale === option.value}
              >
                <span
                  className={styles.languageFlag}
                  style={getFlagStyle(option.value)}
                  aria-hidden="true"
                />
                <span className="sr-only">{option.label}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

export default LanguageSelector
